import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Share2, Map as MapIcon, Table2, Search, Filter, ExternalLink,
  GitBranch, Radar, Crosshair, MapPinOff, X, UserPlus, ArrowUpRight, Mic, FileDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Field, Textarea } from "@/components/common";
import RecordPicker from "@/components/RecordPicker";
import Modal from "@/components/Modal";
import OriginWizard from "@/components/OriginWizard";
import ConnectionModal from "@/components/ConnectionModal";
import VoiceContactModal from "@/components/VoiceContactModal";
import NetworkGraph from "@/components/NetworkGraph";
import NetworkMap from "@/components/NetworkMap";
import { CreateEntityModal } from "@/pages/Entities";

const VIEWS = [{ k: "graph", icon: Share2, l: "Graph" }, { k: "map", icon: MapIcon, l: "Map" }, { k: "table", icon: Table2, l: "Table" }];
const ETYPES = ["person", "business", "organization", "place", "group", "event"];
const VERIFS = ["unverified", "uncertain", "confirmed", "disputed", "outdated"];

export default function SpiderContact() {
  const { workspaceId, canEdit } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [origins, setOrigins] = useState([]);
  const [originId, setOriginId] = useState(params.get("origin") || "");
  const [network, setNetwork] = useState(null);
  const [view, setView] = useState("graph");
  const [selected, setSelected] = useState(null);
  const [selectedFull, setSelectedFull] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [connOpen, setConnOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [highlightPath, setHighlightPath] = useState([]);
  const [filters, setFilters] = useState({ type: "", verification_status: "", rel_status: "", membership: "", q: "" });
  const [loadingNet, setLoadingNet] = useState(false);

  const loadOrigins = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/origins?workspace_id=${workspaceId}&limit=100`);
    setOrigins(data.items);
    return data.items;
  }, [workspaceId]);

  const loadNetwork = useCallback(async (id) => {
    if (!id) { setNetwork(null); return; }
    setLoadingNet(true);
    try {
      const { data } = await api.get(`/origins/${id}/network?workspace_id=${workspaceId}`);
      setNetwork(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoadingNet(false); }
  }, [workspaceId]);

  useEffect(() => { setOrigins([]); loadOrigins(); }, [loadOrigins]);
  useEffect(() => { loadNetwork(originId); setSelected(null); setHighlightPath([]); }, [originId, loadNetwork]);

  const selectOrigin = (id) => { setOriginId(id); setParams(id ? { origin: id } : {}); };

  const onSelectNode = async (node) => {
    if (node.kind === "origin") return;
    setSelected(node);
    setHighlightPath([network.origin.id, node.id]);
    try {
      const { data } = await api.get(`/entities/${node.id}/full?workspace_id=${workspaceId}`);
      setSelectedFull(data);
    } catch { setSelectedFull(null); }
  };

  const filteredEntities = useMemo(() => {
    if (!network) return [];
    return network.entities.filter((e) => {
      if (filters.type && e.type !== filters.type) return false;
      if (filters.verification_status && e.verification_status !== filters.verification_status) return false;
      if (filters.membership && e.membership_review_status !== filters.membership) return false;
      if (filters.q && !(`${e.name} ${(e.aliases || []).join(" ")} ${(e.tags || []).join(" ")}`.toLowerCase().includes(filters.q.toLowerCase()))) return false;
      return true;
    });
  }, [network, filters]);

  const filteredNetwork = useMemo(() => {
    if (!network) return null;
    const ids = new Set(filteredEntities.map((e) => e.id));
    ids.add(network.origin.id);
    return {
      origin: network.origin,
      nodes: network.nodes.filter((n) => ids.has(n.id)),
      links: network.links.filter((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;
        if (filters.rel_status && l.kind === "relationship" && l.status !== filters.rel_status) return false;
        return ids.has(s) && ids.has(t);
      }),
      entities: filteredEntities,
    };
  }, [network, filteredEntities, filters]);

  const noCoords = useMemo(() => filteredEntities.filter((e) => e.lat == null || e.lng == null), [filteredEntities]);

  const removeMembership = async (membershipId, entityId) => {
    try {
      const ms = await api.get(`/memberships?workspace_id=${workspaceId}`);
      // find membership for this entity+origin
    } catch {}
  };

  const promote = async (entityId) => {
    try {
      const { data } = await api.post(`/entities/${entityId}/promote-to-origin?workspace_id=${workspaceId}`);
      toast.success("Created origin from entity");
      await loadOrigins();
      selectOrigin(data.id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const activeOrigin = origins.find((o) => o.id === originId);

  return (
    <div className="flex flex-col" style={{ height: "100%" }}>
      {/* header */}
      <div className="flex items-center gap-3 px-5 py-3 flex-wrap" style={{ borderBottom: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
        <div className="flex items-center gap-2">
          <Share2 size={18} strokeWidth={1.5} style={{ color: "var(--si-teal-light)" }} />
          <span className="font-medium" style={{ color: "var(--si-text)" }}>Spider Contact</span>
        </div>
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Select data-testid="origin-select" value={originId} onChange={(e) => selectOrigin(e.target.value)}>
            <option value="">— Select an origin —</option>
            {origins.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.entity_count})</option>)}
          </Select>
        </div>
        {originId && <Btn variant="ghost" data-testid="origin-report-btn" title="Spider Origin PDF report" onClick={() => window.open(`${api.defaults.baseURL}/reports/origin/${originId}.pdf?workspace_id=${workspaceId}`, "_blank")}><FileDown size={15} /> Origin report</Btn>}
        {canEdit && <Btn variant="secondary" data-testid="spider-voice-btn" onClick={() => setVoiceOpen(true)}><Mic size={15} /> Voice</Btn>}
        {canEdit && <Btn variant="secondary" data-testid="spider-manual-btn" onClick={() => setManualOpen(true)}><UserPlus size={15} /> Enter Manually</Btn>}
        {canEdit && <Btn variant="primary" data-testid="new-origin-btn" onClick={() => setWizardOpen(true)}><Plus size={15} /> New Origin</Btn>}
      </div>

      {!originId ? (
        <div className="flex-1 overflow-y-auto p-6">
          {origins.length === 0 ? (
            <EmptyState icon={Share2} title="No networks yet"
              hint="A Spider Contact network begins with a point of origin — a place, website, person, business, organization, or event."
              action={canEdit && <Btn variant="primary" onClick={() => setWizardOpen(true)}><Plus size={15} /> Create your first origin</Btn>} />
          ) : (
            <div className="max-w-5xl mx-auto">
              <div className="si-label mb-4">Select a network to explore</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {origins.map((o) => (
                  <button key={o.id} data-testid={`origin-card-${o.id}`} onClick={() => selectOrigin(o.id)}
                    className="text-left p-5 transition-colors duration-200"
                    style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--si-surface-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "var(--si-surface)")}>
                    <div className="flex items-center justify-between">
                      <Badge value={o.type} />
                      <span className="font-mono text-sm" style={{ color: "var(--si-silver)" }}>{o.entity_count}</span>
                    </div>
                    <div className="mt-3 text-base" style={{ color: "var(--si-text)" }}>{o.name}</div>
                    <div className="mt-1 text-sm line-clamp-2" style={{ color: "var(--si-text-2)" }}>{o.purpose || "No purpose set."}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex min-h-0">
          {/* main */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* controls */}
            <div className="flex items-center gap-2 px-4 py-2 flex-wrap" style={{ borderBottom: "1px solid var(--si-border)" }}>
              <div className="flex" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
                {VIEWS.map((v) => (
                  <button key={v.k} data-testid={`view-${v.k}`} onClick={() => setView(v.k)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors duration-200"
                    style={{ background: view === v.k ? "var(--si-surface-hover)" : "transparent", color: view === v.k ? "var(--si-text)" : "var(--si-text-2)" }}>
                    <v.icon size={14} strokeWidth={1.5} /> {v.l}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[160px] max-w-xs">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--si-text-2)" }} />
                <Input data-testid="network-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Filter entities…" style={{ paddingLeft: 30 }} />
              </div>
              <Select data-testid="filter-type" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} style={{ width: "auto" }}>
                <option value="">All types</option>
                {ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              <Select data-testid="filter-verif" value={filters.verification_status} onChange={(e) => setFilters((f) => ({ ...f, verification_status: e.target.value }))} style={{ width: "auto" }}>
                <option value="">All verification</option>
                {VERIFS.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              {canEdit && <>
                <Btn variant="secondary" data-testid="add-existing-btn" onClick={() => setAddOpen(true)}><UserPlus size={14} /> Add entity</Btn>
                <Btn variant="ghost" data-testid="discovery-btn" onClick={() => setDiscoveryOpen(true)}><Radar size={14} /> Discovery</Btn>
              </>}
            </div>

            {/* view area */}
            <div className="flex-1 min-h-0 relative">
              {loadingNet || !filteredNetwork ? <Loading /> : (
                <>
                  {view === "graph" && (filteredNetwork.nodes.length <= 1
                    ? <EmptyState icon={Share2} title="No entities in this network yet" hint="Add an existing entity or create a new one to populate the network." />
                    : <NetworkGraph data={filteredNetwork} selectedId={selected?.id} highlightPath={highlightPath} onSelect={onSelectNode} />)}
                  {view === "map" && (
                    <div className="flex flex-col h-full">
                      <div className="flex-1 min-h-0">
                        {activeOrigin?.lat == null && network.entities.every((e) => e.lat == null)
                          ? <EmptyState icon={MapIcon} title="No geographic coordinates available" hint="This origin and its entities have no coordinates. Add latitude/longitude to place them on the map. The table view remains fully functional." />
                          : <NetworkMap origin={filteredNetwork.origin} nodes={filteredNetwork.nodes} onSelect={onSelectNode} selectedId={selected?.id} />}
                      </div>
                      {noCoords.length > 0 && (
                        <div className="px-4 py-2 max-h-28 overflow-y-auto" style={{ borderTop: "1px solid var(--si-border)" }}>
                          <div className="si-label mb-1 flex items-center gap-1"><MapPinOff size={11} /> {noCoords.length} records without coordinates (not on map)</div>
                          <div className="flex flex-wrap gap-1.5">
                            {noCoords.map((e) => (
                              <button key={e.id} onClick={() => onSelectNode(e)} className="text-xs font-mono px-2 py-0.5" style={{ border: "1px solid var(--si-border)", color: "var(--si-text-2)", borderRadius: 2 }}>{e.name}</button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {view === "table" && (
                    <div className="h-full overflow-y-auto overflow-x-auto">
                      <table className="w-full text-sm" data-testid="network-table">
                        <thead className="sticky top-0" style={{ background: "var(--si-surface)" }}>
                          <tr style={{ borderBottom: "1px solid var(--si-border)" }}>
                            {["Name", "Type", "Verification", "Stage", "Distance", "Discovery path", ""].map((h) => <th key={h} className="text-left px-4 py-2 si-label font-normal">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEntities.map((e) => (
                            <tr key={e.id} data-testid={`net-row-${e.id}`} onClick={() => onSelectNode(e)} className="cursor-pointer transition-colors duration-200"
                              style={{ borderBottom: "1px solid var(--si-border)", background: selected?.id === e.id ? "var(--si-surface-hover)" : "transparent" }}>
                              <td className="px-4 py-2" style={{ color: "var(--si-text)" }}>{e.name}</td>
                              <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{e.type}</td>
                              <td className="px-4 py-2"><Badge value={e.verification_status} /></td>
                              <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{(e.relationship_stage || "").replace(/_/g, " ")}</td>
                              <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--si-silver)" }}>{e.distance_m != null ? `${e.distance_m}m` : "—"}</td>
                              <td className="px-4 py-2 text-xs" style={{ color: "var(--si-text-2)" }}>{e.discovery_path || "—"}</td>
                              <td className="px-4 py-2 text-right"><Link to={`/app/entities/${e.id}`} onClick={(ev) => ev.stopPropagation()} style={{ color: "var(--si-silver)" }}><ExternalLink size={14} /></Link></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredEntities.length === 0 && <EmptyState icon={Table2} title="No entities match" hint="Adjust filters or add entities to this network." />}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* right sidebar */}
          <aside className="hidden lg:flex flex-col" style={{ width: 340, borderLeft: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
            {!selected ? (
              <div className="p-5">
                <div className="si-label mb-3">Origin</div>
                <div className="text-lg" style={{ color: "var(--si-text)" }}>{network?.origin.name}</div>
                <div className="mt-1"><Badge value={network?.origin.type} /></div>
                <p className="text-sm mt-3" style={{ color: "var(--si-text-2)" }}>{network?.origin.purpose || "No purpose set."}</p>
                <div className="mt-4 space-y-1.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>
                  <div><span className="si-label">entities</span> {network?.entities.length}</div>
                  <div><span className="si-label">boundary</span> {network?.origin.boundary_kind}{network?.origin.boundary_kind === "radius" ? ` · ${network?.origin.radius_m}m` : ""}</div>
                  {network?.origin.coverage_notes && <div><span className="si-label">coverage</span> {network.origin.coverage_notes}</div>}
                  {network?.origin.unresolved_areas && <div style={{ color: "var(--si-warning)" }}><span className="si-label">unresolved</span> {network.origin.unresolved_areas}</div>}
                </div>
                <p className="text-xs mt-6" style={{ color: "var(--si-text-2)" }}>Select a node in the graph, a marker on the map, or a table row to inspect an entity and its documented path to the origin.</p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--si-border)" }}>
                  <div>
                    <div className="text-base" style={{ color: "var(--si-text)" }}>{selected.label || selected.name}</div>
                    <div className="mt-1 flex gap-1.5"><Badge value={selected.type} /><Badge value={selected.verification_status} /></div>
                  </div>
                  <button onClick={() => { setSelected(null); setHighlightPath([]); }} style={{ color: "var(--si-text-2)" }}><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
                  <div className="p-3 font-mono text-xs" style={{ background: "var(--si-bg)", border: "1px solid var(--si-teal)", borderRadius: 2, color: "var(--si-silver)" }}>
                    <div className="si-label mb-1 flex items-center gap-1"><Crosshair size={11} /> Path to origin</div>
                    {network.origin.name} → {selected.label || selected.name}
                  </div>
                  {selectedFull && (
                    <>
                      <div>
                        <div className="si-label mb-1.5">Memberships ({selectedFull.memberships.length})</div>
                        {selectedFull.memberships.map((m) => <div key={m.id} className="text-xs" style={{ color: "var(--si-text-2)" }}>· {m.origin_name}</div>)}
                      </div>
                      <div>
                        <div className="si-label mb-1.5">Relationships ({selectedFull.relationships.length})</div>
                        {selectedFull.relationships.map((r) => (
                          <div key={r.id} className="text-xs flex items-center gap-1" style={{ color: "var(--si-text-2)" }}>
                            <GitBranch size={11} /> {r.source_name} <span className="font-mono" style={{ color: "var(--si-silver)" }}>{r.type.replace(/_/g, " ")}</span> {r.target_name} <Badge value={r.status} />
                          </div>
                        ))}
                        {selectedFull.relationships.length === 0 && <div className="text-xs" style={{ color: "var(--si-text-2)" }}>None documented.</div>}
                      </div>
                      <div>
                        <div className="si-label mb-1.5">Evidence ({selectedFull.evidence.length})</div>
                        {selectedFull.evidence.map((ev) => (
                          <div key={ev.id} className="text-xs mb-1" style={{ color: "var(--si-text-2)" }}>
                            <Badge value={ev.verification_status} /> <span className="ml-1">{ev.excerpt || ev.source_url}</span>
                          </div>
                        ))}
                        {selectedFull.evidence.length === 0 && <div className="text-xs" style={{ color: "var(--si-text-2)" }}>None attached.</div>}
                      </div>
                    </>
                  )}
                </div>
                <div className="p-4 space-y-2" style={{ borderTop: "1px solid var(--si-border)" }}>
                  <Link to={`/app/entities/${selected.id}`}><Btn variant="secondary" className="w-full"><ExternalLink size={14} /> Open entity file</Btn></Link>
                  {canEdit && <Btn variant="secondary" className="w-full" data-testid="sidebar-add-connection" onClick={() => setConnOpen(true)}><GitBranch size={14} /> Add connection</Btn>}
                  {canEdit && <Btn variant="ghost" className="w-full" data-testid="promote-btn" onClick={() => promote(selected.id)}><ArrowUpRight size={14} /> Promote to new origin</Btn>}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      <OriginWizard open={wizardOpen} onClose={() => setWizardOpen(false)} workspaceId={workspaceId}
        onCreated={async (o) => { await loadOrigins(); selectOrigin(o.id); }} />
      {addOpen && <AddEntityModal workspaceId={workspaceId} originId={originId} onClose={() => setAddOpen(false)}
        onAdded={() => { setAddOpen(false); loadNetwork(originId); loadOrigins(); }} />}
      {discoveryOpen && <DiscoveryModal workspaceId={workspaceId} originId={originId} onClose={() => setDiscoveryOpen(false)} />}
      {connOpen && selected && <ConnectionModal workspaceId={workspaceId} source={{ id: selected.id, name: selected.label || selected.name }}
        onClose={() => setConnOpen(false)} onSaved={() => { setConnOpen(false); onSelectNode(selected); loadNetwork(originId); }} />}
      {voiceOpen && <VoiceContactModal workspaceId={workspaceId} originId={originId || null}
        onClose={() => setVoiceOpen(false)} onSaved={() => { loadNetwork(originId); loadOrigins(); }} />}
      {manualOpen && <CreateEntityModal workspaceId={workspaceId} defaultType="person"
        defaultOriginId={originId || ""} contextLabel={originId ? "Will be added to the current Spider network" : ""}
        onClose={() => setManualOpen(false)}
        onCreated={(id) => { setManualOpen(false); if (originId) loadNetwork(originId); navigate(`/app/entities/${id}`); }} />}
    </div>
  );
}

function AddEntityModal({ workspaceId, originId, onClose, onAdded }) {
  const [tab, setTab] = useState("existing");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [nf, setNf] = useState({ name: "", type: "business", description: "", address: "", lat: "", lng: "" });
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    const { data } = await api.get(`/entities?workspace_id=${workspaceId}&search=${encodeURIComponent(q)}&limit=25`);
    setResults(data.items);
  }, [q, workspaceId]);
  useEffect(() => { search(); }, [search]);

  const addExisting = async (entityId) => {
    setBusy(true);
    try {
      await api.post(`/memberships?workspace_id=${workspaceId}`, { origin_id: originId, entity_id: entityId, discovery_path: "Added manually", source: "Manual" });
      toast.success("Added to network");
      onAdded();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createNew = async () => {
    if (!nf.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const { data } = await api.post(`/entities?workspace_id=${workspaceId}`, {
        ...nf, lat: nf.lat === "" ? null : parseFloat(nf.lat), lng: nf.lng === "" ? null : parseFloat(nf.lng),
      });
      await api.post(`/memberships?workspace_id=${workspaceId}`, { origin_id: originId, entity_id: data.id, discovery_path: "Manual entry", source: "Manual" });
      toast.success("Entity created & added");
      onAdded();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Add entity to network" testid="add-entity-modal">
      <div className="flex gap-1 mb-4" style={{ border: "1px solid var(--si-border)", borderRadius: 2, width: "fit-content" }}>
        {[["existing", "Add existing"], ["new", "Create new"]].map(([k, l]) => (
          <button key={k} data-testid={`add-tab-${k}`} onClick={() => setTab(k)} className="px-3 py-1.5 text-sm transition-colors duration-200"
            style={{ background: tab === k ? "var(--si-surface-hover)" : "transparent", color: tab === k ? "var(--si-text)" : "var(--si-text-2)" }}>{l}</button>
        ))}
      </div>
      {tab === "existing" ? (
        <div>
          <div className="mb-3"><RecordPicker workspaceId={workspaceId} label="Add which contact to this network? (yourself via Me, or jump to a contact)" value={null} onChange={(rec) => rec && addExisting(rec.id)} allowMe allowUnset testid="spider-quick-add" /></div>
          <Input data-testid="add-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search existing entities…" autoFocus />
          <div className="mt-3 max-h-72 overflow-y-auto">
            {results.map((e) => (
              <div key={e.id} className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div><div className="text-sm" style={{ color: "var(--si-text)" }}>{e.name}</div><div className="si-label">{e.type}</div></div>
                <Btn variant="secondary" className="!py-1 !px-2 text-xs" disabled={busy} onClick={() => addExisting(e.id)} data-testid={`add-existing-${e.id}`}>Add</Btn>
              </div>
            ))}
            {results.length === 0 && <div className="text-sm py-6 text-center" style={{ color: "var(--si-text-2)" }}>No matching entities. Switch to "Create new".</div>}
          </div>
        </div>
      ) : (
        <div>
          <Field label="Name"><Input data-testid="new-entity-name" value={nf.name} onChange={(e) => setNf((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Business in adjoining unit — name unconfirmed" /></Field>
          <Field label="Type"><Select value={nf.type} onChange={(e) => setNf((p) => ({ ...p, type: e.target.value }))}>{ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
          <Field label="Description"><Textarea value={nf.description} onChange={(e) => setNf((p) => ({ ...p, description: e.target.value }))} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Address"><Input value={nf.address} onChange={(e) => setNf((p) => ({ ...p, address: e.target.value }))} /></Field>
            <Field label="Lat"><Input value={nf.lat} onChange={(e) => setNf((p) => ({ ...p, lat: e.target.value }))} /></Field>
            <Field label="Lng"><Input value={nf.lng} onChange={(e) => setNf((p) => ({ ...p, lng: e.target.value }))} /></Field>
          </div>
          <Btn variant="primary" className="w-full" disabled={busy} onClick={createNew} data-testid="new-entity-save">{busy ? "Saving…" : "Create & add to network"}</Btn>
        </div>
      )}
    </Modal>
  );
}

function DiscoveryModal({ workspaceId, originId, onClose }) {
  const [status, setStatus] = useState(null);
  const [seed, setSeed] = useState("");
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get("/discovery/status"),
      api.get(`/discovery/runs?workspace_id=${workspaceId}&origin_id=${originId}`),
    ]);
    setStatus(s.data); setRuns(r.data);
  }, [workspaceId, originId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/discovery/runs?workspace_id=${workspaceId}`, {
        origin_id: originId, source: "web_crawler", seed_url: seed, allowed_domains: [],
        max_depth: 1, max_pages: 20, max_results: 50, max_seconds: 60,
      });
      toast.message("Discovery attempted — source not connected", { description: data.coverage_notes });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="External Discovery" width={580} testid="discovery-modal">
      {!status ? <Loading /> : (
        <div className="space-y-4">
          {status.connections.map((c) => (
            <div key={c.id} className="p-4" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
              <div className="flex items-center justify-between">
                <div className="text-sm" style={{ color: "var(--si-text)" }}>{c.name}</div>
                <Badge value={c.connected ? "confirmed" : "disputed"}>{c.connected ? "Connected" : "Not connected"}</Badge>
              </div>
              <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>{c.instructions}</div>
            </div>
          ))}
          <div className="p-4" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
            <div className="si-label mb-2">URL discovery — define scope</div>
            <Input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="Seed URL (e.g. https://directory.example)" />
            <div className="text-xs mt-2 mb-3" style={{ color: "var(--si-text-2)" }}>Scope: depth 1 · max 20 pages · max 50 results · 60s. Private networks and unsafe schemes are blocked. The crawler is <strong style={{ color: "var(--si-warning)" }}>not connected</strong>; running records an honest failure and preserves the manual workflow.</div>
            <Btn variant="secondary" onClick={run} disabled={busy} data-testid="run-discovery-btn"><Radar size={14} /> {busy ? "Running…" : "Run discovery"}</Btn>
          </div>
          {runs.length > 0 && (
            <div>
              <div className="si-label mb-2">Recent runs</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {runs.map((r) => (
                  <div key={r.id} className="text-xs font-mono p-2" style={{ border: "1px solid var(--si-border)", color: "var(--si-text-2)", borderRadius: 2 }}>
                    <Badge value="disputed">{r.status}</Badge> <span className="ml-1">{new Date(r.started_at).toLocaleString()}</span>
                    <div className="mt-1">{r.coverage_notes}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
